import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  AdminPermRow,
  AdminRoleCreateRequest,
  AdminRoleRow,
  AdminUserCreateRequest,
  AdminUserCreateResult,
  AdminUserPreflightQuery,
  AdminUserPreflightResult,
  AdminUserQuery,
  AdminUserRow,
  AdminUserUpdateRequest
} from '../types/admin';
import type { OrgConfigItem, OrgConfigResult, OrgConfigSaveResult } from '../types/auth';
import type { PageResult } from '../types/result';

/** 系统管理：用户 / 角色 / 权限（需 sys:user:manage、sys:role:manage 或管理员）。 */
export function createAdminApi(http: HttpClient) {
  return {
    users: {
      list(query: AdminUserQuery = {}): Promise<PageResult<AdminUserRow>> {
        return http.request<PageResult<AdminUserRow>>({ path: '/sys/user', query: toQuery(query) });
      },
      /** 新建用户前的预检（不占号）：预览自动账号 + 邮箱重复数。 */
      preflight(query: AdminUserPreflightQuery = {}): Promise<AdminUserPreflightResult> {
        return http.request<AdminUserPreflightResult>({ path: '/sys/user/preflight', query: toQuery(query) });
      },
      /** 账号不受理客户端传入（服务端按「机构号+4 位序号」生成）。 */
      create(body: AdminUserCreateRequest): Promise<AdminUserCreateResult> {
        return http.request<AdminUserCreateResult>({ path: '/sys/user', method: 'POST', body });
      },
      update(id: Id, body: AdminUserUpdateRequest): Promise<{ id: string }> {
        return http.request<{ id: string }>({ path: `/sys/user/${id}`, method: 'PUT', body });
      },
      resetPassword(id: Id, password: string): Promise<{ id: string; reset: boolean }> {
        return http.request<{ id: string; reset: boolean }>({
          path: `/sys/user/${id}/password`,
          method: 'POST',
          body: { password }
        });
      },
      roles(id: Id): Promise<AdminRoleRow[]> {
        return http.request<AdminRoleRow[]>({ path: `/sys/user/${id}/roles` });
      },
      setRoles(id: Id, roleIds: Id[]): Promise<{ id: string; roleIds: Id[] }> {
        return http.request<{ id: string; roleIds: Id[] }>({
          path: `/sys/user/${id}/roles`,
          method: 'PUT',
          body: { roleIds }
        });
      }
    },

    roles: {
      list(): Promise<AdminRoleRow[]> {
        return http.request<AdminRoleRow[]>({ path: '/sys/role' });
      },
      create(body: AdminRoleCreateRequest): Promise<{ id: string; roleCode: string }> {
        return http.request<{ id: string; roleCode: string }>({ path: '/sys/role', method: 'POST', body });
      },
      perms(id: Id): Promise<Id[]> {
        return http.request<Id[]>({ path: `/sys/role/${id}/perms` });
      },
      setPerms(id: Id, permIds: Id[]): Promise<{ id: string; permIds: Id[] }> {
        return http.request<{ id: string; permIds: Id[] }>({
          path: `/sys/role/${id}/perms`,
          method: 'PUT',
          body: { permIds }
        });
      }
    },

    perms: {
      list(): Promise<AdminPermRow[]> {
        return http.request<AdminPermRow[]>({ path: '/sys/permission' });
      }
    },

    /** 机构个性化配置（`sys_org_config`，门禁 sys:config:manage）：配色/标题/业务开关。 */
    orgConfig: {
      get(orgId: Id): Promise<OrgConfigResult> {
        return http.request<OrgConfigResult>({ path: `/sys/org-config/${orgId}` });
      },
      save(orgId: Id, items: OrgConfigItem[]): Promise<OrgConfigSaveResult> {
        return http.request<OrgConfigSaveResult>({
          path: `/sys/org-config/${orgId}`,
          method: 'PUT',
          body: { items }
        });
      },
      remove(orgId: Id, key: string): Promise<OrgConfigSaveResult> {
        return http.request<OrgConfigSaveResult>({
          path: `/sys/org-config/${orgId}/${encodeURIComponent(key)}`,
          method: 'DELETE'
        });
      }
    }
  };
}

export type AdminApi = ReturnType<typeof createAdminApi>;
